"use client";

import { useBuilder } from "@/context/BuilderContext";
import { formatPrice } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { Check } from "lucide-react";
import { useBuilderOptions } from "@/hooks/useBuilderOptions";

export function StepLength() {
  const { selections, setOption } = useBuilder();
  const selected = selections.length;
  const { options } = useBuilderOptions();
  const dbPrices = new Map((options.length || []).map((o) => [o.value, o.price]));

  const LENGTHS = [
    {
      value: '16"',
      price: dbPrices.get('16"') ?? 0,
      label: '16"',
      description: "Shoulder length",
      heightPercent: 32,
    },
    {
      value: '20"',
      price: dbPrices.get('20"') ?? 400,
      label: '20"',
      description: "Below shoulder",
      heightPercent: 50,
    },
    {
      value: '24"',
      price: dbPrices.get('24"') ?? 800,
      label: '24"',
      description: "Mid-back length",
      heightPercent: 68,
    },
    {
      value: '30"',
      price: dbPrices.get('30"') ?? 1400,
      label: '30"',
      description: "Lower back",
      heightPercent: 88,
    },
    {
      value: '32"',
      price: dbPrices.get('32"') ?? 1800,
      label: '32"',
      description: "Waist length",
      heightPercent: 100,
    },
  ];

  return (
    <div className="pt-1 space-y-3 sm:space-y-4">
      {/* Helper text — small on mobile */}
      <div className="rounded-lg border border-brand-gold/15 bg-brand-cream/40 px-3 py-2 sm:px-4 sm:py-3">
        <p className="text-[11px] sm:text-xs leading-relaxed text-brand-dark/70">
          Measured from crown to ends. Longer lengths add cost.
        </p>
      </div>

      {/* Length cards — vertical stack, compact on mobile */}
      <div className="space-y-2 sm:space-y-3">
        {LENGTHS.map((option) => {
          const isSelected = selected === option.value;

          return (
            <button
              key={option.value}
              onClick={() => setOption("length", option.value, option.price)}
              className={cn(
                "relative w-full flex items-center gap-3 sm:gap-5 px-3 py-3 sm:px-5 sm:py-4 rounded-xl border-2 transition-all duration-300 active:scale-[0.99] text-start min-h-[60px]",
                "hover:shadow-md",
                isSelected
                  ? "border-brand-gold bg-brand-gold/[0.05] shadow-md"
                  : "border-brand-dark/10 bg-white hover:border-brand-gold-light"
              )}
            >
              {/* Visual length bar — smaller on mobile */}
              <div className="relative flex flex-col items-center w-8 sm:w-12 shrink-0">
                <div
                  className={cn(
                    "w-5 h-5 sm:w-7 sm:h-7 rounded-full border-2 flex-shrink-0 z-10 transition-colors duration-300",
                    isSelected
                      ? "border-brand-gold bg-brand-gold/20"
                      : "border-brand-dark/20 bg-brand-cream"
                  )}
                />
                <div
                  className={cn(
                    "w-4 sm:w-6 rounded-b-full -mt-0.5 transition-all duration-500",
                    isSelected
                      ? "bg-gradient-to-b from-brand-gold via-brand-gold to-brand-gold-dark"
                      : "bg-gradient-to-b from-brand-dark/15 to-brand-dark/30"
                  )}
                  style={{ height: `${option.heightPercent * 0.45}px` }}
                />
              </div>

              {/* Length name + description */}
              <div className="flex-1 min-w-0">
                <p className={cn(
                  "font-display text-xl sm:text-2xl tracking-tight leading-none transition-colors duration-300",
                  isSelected ? "text-brand-dark" : "text-brand-dark/85"
                )}>
                  {option.label}
                </p>
                <p className="text-[11px] sm:text-xs text-brand-muted mt-0.5 sm:mt-1">
                  {option.description}
                </p>
              </div>

              {/* Price */}
              <div className="flex-shrink-0 text-end pe-7 sm:pe-8">
                <span className={cn(
                  "text-sm sm:text-base font-semibold whitespace-nowrap",
                  option.price === 0
                    ? "text-brand-gold"
                    : isSelected ? "text-brand-dark" : "text-brand-dark/70"
                )}>
                  {option.price === 0 ? "Included" : `+${formatPrice(option.price)}`}
                </span>
              </div>

              {/* Selected check */}
              {isSelected && (
                <div className="absolute top-1/2 -translate-y-1/2 end-2 sm:end-3 w-5 h-5 sm:w-6 sm:h-6 bg-brand-gold rounded-full flex items-center justify-center shadow-sm animate-scale-in">
                  <Check className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-white" strokeWidth={3} />
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
